import { useQuery } from '@tanstack/react-query'
import { Fragment, useEffect, useMemo } from 'react'
import { generatePath, Link, useLocation, useParams } from 'react-router'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb'
import { ArrowLeftIcon } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { AttendeeDetailCtaPanel } from '@/features/marquee/components/attendee-detail-cta-panel'
import { AttendeeDetailHostRail } from '@/features/marquee/components/attendee-detail-host-rail'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import {
	attendeesQueryOptions,
	ctasQueryOptions,
	eventQueryOptions
} from '@/features/marquee/query-options'
import { type CtaCaptureBlock, ctaCaptureBlock } from '@/features/marquee/utils'
import { routes } from '@/routes'
import type { MarqueeAttendee, MarqueeEventState } from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

function MarqueeAttendeeDetail() {
	const { t } = useTranslation()
	const { id, attendeeId } = useParams<{ id: string; attendeeId: string }>()

	// The roster this page is opened from is long, and the document is what
	// scrolls (MarqueeLayout is min-h-dvh with no overflow container), so a tap
	// on a row near the bottom of the list rendered this page still scrolled
	// down — often past its end, since the detail page is the shorter of the
	// two. Nothing in the app resets scroll on navigation, so this page does it
	// for itself rather than the router doing it for every route.
	//
	// Keyed on attendeeId, not just mount: moving between two attendees keeps
	// this component mounted, and that transition needs the reset just as much.
	// The id is read inside the effect so it is a genuine dependency rather than
	// one the exhaustive-deps rule would strip back to mount-only.
	// `instant` because a scroll animation on arrival reads as the page moving
	// under the operator; `html` carries scroll-smooth, which this overrides.
	useEffect(() => {
		if (attendeeId) {
			window.scrollTo({ top: 0, behavior: 'instant' })
		}
	}, [attendeeId])

	const { data: event } = useQuery(eventQueryOptions(id))
	const { data: ctas = [] } = useQuery(ctasQueryOptions())

	// There is no attendee-detail endpoint and there is not going to be one
	// (OPSSTD-4180 pins "no new endpoint"), so the page composes its view-model
	// from the roster row the user clicked. Reusing the roster query is what
	// keeps name/company/host-id in sync with that row.
	const {
		data: attendees,
		isLoading,
		isError
	} = useQuery(attendeesQueryOptions(id))
	const attendee = useMemo(
		() => attendees?.rows?.find((row) => row.id === attendeeId),
		[attendees, attendeeId]
	)
	// Roster loaded but the id isn't in it: drives the not-found state.
	const notFound = !(isLoading || isError || attendee)

	// The page exists for a tune-up only, which is why the roster tables leave
	// the name unlinked on a presentation; this covers arriving by URL. Stays
	// false while the event loads, so the skeleton shows instead of a flash of
	// the blocked message.
	const isPresentation = event ? event.capture_mode !== 'tune_up' : false

	const ctaOptions = useMemo<ComboboxOption[]>(
		() => ctas.map((cta) => ({ value: cta.code, label: cta.label })),
		[ctas]
	)

	const location = useLocation()

	// The row link stashes its tab path in state.from, so we know which table to
	// return to (and how to label it). Fallback: the event index route, which
	// redirects to the state-appropriate tab.
	const fromPath =
		typeof location.state?.from === 'string'
			? location.state.from
			: undefined
	const originTab = [
		{
			route: routes.MARQUEE_EVENT_ATTENDEES,
			label: t('marquee:tab_attendees')
		},
		{
			route: routes.MARQUEE_EVENT_CHECK_IN,
			label: t('marquee:tab_check_in')
		},
		{ route: routes.MARQUEE_EVENT_EXPORT, label: t('marquee:tab_export') }
	]
		.map((tab) => ({
			to: generatePath(tab.route.handle.fullPath, { id: id ?? '' }),
			label: tab.label
		}))
		.find((tab) => tab.to === fromPath)

	const eventIndexPath = generatePath(
		routes.MARQUEE_EVENT_DETAIL.handle.fullPath,
		{ id: id ?? '' }
	)
	const backTo = originTab?.to ?? eventIndexPath
	const backLabel = originTab
		? t('marquee:attendee_detail_back', { tab: originTab.label })
		: t('marquee:attendee_detail_back_fallback')

	const crumbs = [
		{
			label: t('marquee:breadcrumb_event_list'),
			to: routes.MARQUEE_EVENTS.handle.fullPath
		},
		{ label: event?.name ?? '…', to: eventIndexPath },
		...(originTab ? [{ label: originTab.label, to: originTab.to }] : [])
	]

	return (
		<div className='flex flex-1 flex-col'>
			<title>{t('marquee:page_title_attendee_detail')}</title>

			{/* Breadcrumb */}
			<div className='border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<Breadcrumb>
					<BreadcrumbList className='gap-2 text-xs'>
						{crumbs.map((crumb) => (
							<Fragment key={`${crumb.label}-${crumb.to}`}>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<Link
											className='text-neutral-900 hover:underline'
											to={crumb.to}
										>
											{crumb.label}
										</Link>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator className='text-neutral-500 [&>svg]:size-3' />
							</Fragment>
						))}
						<BreadcrumbItem>
							<BreadcrumbPage className='text-neutral-500'>
								{attendee ? attendee.name || '-' : '…'}
							</BreadcrumbPage>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
			</div>

			<AttendeeDetailBody
				attendee={attendee}
				backLabel={backLabel}
				backTo={backTo}
				block={ctaCaptureBlock(event, attendee)}
				ctaOptions={ctaOptions}
				eventId={id}
				eventState={event?.state}
				isError={isError}
				isLoading={isLoading}
				isPresentation={isPresentation}
				notFound={notFound}
				timeZone={event?.time_zone}
			/>
		</div>
	)
}

interface AttendeeDetailBodyProps {
	/** The roster row — the whole profile this page renders. */
	attendee: MarqueeAttendee | undefined
	eventId?: string
	/** Lifecycle state, used only to word a wrong-state capture refusal. */
	eventState?: MarqueeEventState
	isLoading: boolean
	isError: boolean
	notFound: boolean
	/** A presentation has no attendee-detail page: only a tune-up captures 1:1. */
	isPresentation: boolean
	/** Why per-attendee capture is unavailable, or `null` when it is allowed. */
	block: CtaCaptureBlock | null
	ctaOptions: ComboboxOption[]
	backTo: string
	backLabel: string
	/** The event's IANA zone: the capture instant is read in the market's clock. */
	timeZone?: string
}

/** Picks the body between not-found, loading, and the loaded two-pane layout. */
function AttendeeDetailBody({
	attendee,
	eventId,
	eventState,
	isLoading,
	isError,
	notFound,
	isPresentation,
	block,
	ctaOptions,
	backTo,
	backLabel,
	timeZone
}: AttendeeDetailBodyProps) {
	if (isPresentation) {
		return (
			<BlockedState
				backLabel={backLabel}
				backTo={backTo}
				messageKey='marquee:attendee_detail_presentation_only'
			/>
		)
	}
	if (isError || notFound) {
		return (
			<BlockedState
				backLabel={backLabel}
				backTo={backTo}
				messageKey='marquee:attendee_detail_not_found'
			/>
		)
	}
	if (isLoading || !attendee) {
		return <LoadingState />
	}
	return (
		<div className='flex flex-1 l:flex-row flex-col'>
			<AttendeeDetailHostRail
				attendee={attendee}
				backLabel={backLabel}
				backTo={backTo}
			/>
			<AttendeeDetailCtaPanel
				attendee={attendee}
				block={block}
				ctaOptions={ctaOptions}
				eventId={eventId}
				eventState={eventState}
				timeZone={timeZone}
			/>
		</div>
	)
}

function LoadingState() {
	return (
		<div className='flex flex-1 l:flex-row flex-col'>
			<div className='flex l:w-[420px] l:shrink-0 flex-col gap-6 bg-grey-100 l:px-10 px-4 pt-6 pb-10'>
				<Skeleton height={20} width={140} />
				<Skeleton className='rounded-full' height={64} width={64} />
				<Skeleton height={24} width={200} />
				<Skeleton height={180} width='100%' />
				<Skeleton height={160} width='100%' />
			</div>
			<div className='flex flex-1 flex-col gap-6 l:px-10 px-4 pt-6 pb-10'>
				<Skeleton height={28} width={260} />
				<Skeleton height={88} width='100%' />
				<Skeleton height={88} width='100%' />
				<Skeleton height={88} width='100%' />
			</div>
		</div>
	)
}

/** Terminal state for the page: why there is nothing to show, plus the way back. */
function BlockedState({
	backTo,
	backLabel,
	messageKey
}: {
	backTo: string
	backLabel: string
	messageKey: TKey
}) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-1 flex-col items-center justify-center gap-4 px-4 py-20 text-center'>
			<p className='font-medium text-lg text-neutral-900'>
				{t(messageKey)}
			</p>
			<Link
				className='flex items-center gap-2 text-neutral-500 text-sm hover:text-neutral-900'
				to={backTo}
			>
				<ArrowLeftIcon className='size-4' />
				{backLabel}
			</Link>
		</div>
	)
}

export { MarqueeAttendeeDetail }
